/**
 * Portal ErpService — a lean Bearer adapter for the external Customer Impact
 * Portal. It hits the same-origin `/api/portal/*` Bearer endpoints (Caddy
 * proxies `/api` to the Core API). Bearer, NOT cookie: there is no
 * `credentials: 'include'`, and the token comes from the PKCE auth module.
 *
 * This is NOT a `@toeverything/infra` DI service — the portal is a separate,
 * lean origin and does not pull `@affine/core`.
 */

export interface ImpactCategory {
  category: string;
  weight_kg: number;
  count: number;
}

export interface PortalImpactReport {
  total_weight_kg: number;
  devices_processed: number;
  drives_wiped: number;
  drives_destroyed: number;
  materials_recovered_by_category: ImpactCategory[];
}

/**
 * §7.7: the four portal-listable item types (the client mirror of the server's
 * `PORTAL_PASSPORT_ITEM_TYPES` — *listable ≡ openable*). The `/portal/assets`
 * `?type=` filter is validated server-side against the same constant.
 */
export type PortalItemType = 'asset' | 'drive' | 'part' | 'commodity_lot';

/**
 * The Portal redact projection (Task 3): the external serializer emits no
 * facility_id / stage / allowed_transitions.
 */
/** Account-level display config (GET /api/portal/profile — non-sensitive booleans only). */
export interface PortalProfile {
  asset_prep_waiver: boolean;
}

export interface PortalRecord {
  id: string;
  type: string;
  title: string | null;
  title_redacted: boolean;
  data: Record<string, unknown>;
  account_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * P3.2: the §8 quote/service request form fields — the portal-submittable
 * allowlist ONLY (the server owns status/quote_no/service_type_internal/
 * account_id and refuses them at the edge). Enum fields carry the registry
 * wire tokens; the view maps them to localized labels.
 */
export interface PortalQuoteRequestInput {
  request_type: 'service_request' | 'quote_request' | 'quote_and_service';
  service_type?:
    | 'value_recovery'
    | 'onsite_data_sanitization'
    | 'onsite_shred'
    | 'equipment_transfer'
    | 'equipment_recycle'
    | 'logistics';
  reference_no?: string;
  po_no?: string;
  company?: string;
  requester_name?: string;
  requester_phone?: string;
  requester_email?: string;
  location_name?: string;
  onsite_contact_name?: string;
  onsite_contact_phone?: string;
  onsite_contact_email?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  notes?: string;
  is_medical_equipment?: 'yes' | 'no' | 'not_applicable';
  is_decontaminated?: 'yes' | 'no' | 'not_applicable';
  distress_reason?:
    | 'fire'
    | 'flood'
    | 'wreck'
    | 'prolonged_storage'
    | 'warehouse_storage'
    | 'other'
    | 'not_applicable';
}

/**
 * P3.3: one deliverable entry from the order detail — the server's redacted
 * projection AUGMENTED with the deliverable kind and the download path the
 * client must use verbatim.
 */
export interface PortalDeliverable extends PortalRecord {
  deliverable_kind: 'certificate' | 'file';
  download_path: string;
}

/** P3.3: `GET /api/portal/orders/:id` — the order + its deliverables. */
export interface PortalOrderDetail {
  order: PortalRecord;
  deliverables: PortalDeliverable[];
}

/**
 * IP5.6: the certificate reference on a passport milestone — the certificate
 * number + the server-built download path ONLY (the 4B.6 rule: the client
 * renders download affordances strictly from server-supplied paths, never
 * derives URLs). Field names mirror the Rust `PortalCertificateRef`
 * (portal_passport.rs, `Option<String> cert_number` — hence nullable) — pinned
 * by tests/portal_parity.rs; never invent wire shapes client-side.
 */
export interface PortalCertificateRef {
  cert_number: string | null;
  download_path: string;
}

/**
 * IP5.6: one customer-visible milestone of the light passport — field-for-field
 * with the Rust `PortalMilestone` (plan §3.8.2). `milestone` is a wire token
 * from the server's fixed PORTAL_PASSPORT_MILESTONES set, `occurred_on` is a
 * DATE (`YYYY-MM-DD`) — never a timestamp — and `time_basis` is only
 * `business` | `unknown`: there is no `recorded` basis on this surface
 * (§3.8.4 — the import stamp must never reach a customer).
 */
export interface PortalMilestone {
  milestone:
    | 'received'
    | 'sorting'
    | 'dismantling'
    | 'wiping'
    | 'destruction_certificate'
    | 'sold';
  occurred_on: string | null;
  /** The full UTC instant, present ONLY when the evidence genuinely recorded a
   *  clock time (owner 2026-08-26) — a date-only origin carries none. */
  occurred_at?: string | null;
  time_basis: 'business' | 'unknown';
  certificate: PortalCertificateRef | null;
}

/**
 * IP5.6: `GET /api/portal/items/:id/passport` — the customer light passport
 * read-model, field-for-field with the Rust `PortalItemPassport` (plan §3.8.2).
 * `sold_visible` is the server-derived entitlement echo (§3.8.3) — the UI
 * renders the explanation copy from it and never infers entitlement itself.
 */
export interface PortalItemPassport {
  item_id: string;
  description: string | null;
  order_no: string | null;
  milestones: PortalMilestone[];
  sold_visible: boolean;
}

/**
 * Server-side narrowing accepted by the portal list endpoints (validated
 * server-side — free text matches ONLY portal-disclosed fields, and dates are
 * business-date axes; an unsupported param is a 400, never silently ignored).
 */
export interface PortalListParams {
  q?: string;
  /** Which disclosed business date the range keys on. Orders: a
   *  `PORTAL_ORDER_DATE_FIELDS` name (server default `received_date`).
   *  Certificates: `service_date` (default) or `issued_at`. */
  dateField?:
    | 'received_date'
    | 'completion_date'
    | 'scheduled_date'
    | 'dispatch_date'
    | 'service_date'
    | 'issued_at';
  /** YYYY-MM-DD (inclusive). */
  from?: string;
  /** YYYY-MM-DD (inclusive). */
  to?: string;
}

/** The CSV export kinds (`GET /api/portal/export?kind=` — server-validated). */
export type PortalExportKind = 'orders' | 'items' | 'requests' | 'certificates';

/**
 * One certificate row from `GET /api/portal/certificates` — the redacted
 * Portal projection augmented (when derivable) with the server-built
 * `download_path` (the 4B.6 rule: the client renders download affordances
 * strictly from server-supplied paths).
 */
export interface PortalCertificate extends PortalRecord {
  download_path?: string;
  /** The parent order's customer-facing number (portal-public on /orders —
   *  a truncated cert-row uuid means nothing to a customer). */
  order_no?: string;
  /** The parent order's `received_date` (YYYY-MM-DD) — the SERVICE date the
   *  default date filter keys on. Absent when the order carries none
   *  (rendered '—', never an invented date). */
  service_date?: string;
}

/**
 * One item row from `GET /api/portal/assets` — the redacted Portal projection
 * augmented with the parent order's number + RAW receive moment (owner
 * 2026-08-26: the list's date column showed the import stamp under a
 * "Received" label; both augments are already portal-public on /orders).
 */
export interface PortalItem extends PortalRecord {
  order_no?: string;
  /** The parent order's `received_date` verbatim — a full UTC timestamp on
   *  most legacy orders, a bare date where ROMS recorded only the day. */
  order_received_at?: string;
}

/** A binary download + the server's no-silent-partial signal
 *  (`X-Export-Truncated`) the UI must surface, never swallow. */
export interface PortalDownload {
  blob: Blob;
  truncated: boolean;
}

/** Build the `?…` query string for the portal list/export params. */
function portalQuery(
  params?: PortalListParams,
  type?: string,
  kind?: PortalExportKind
): string {
  const usp = new URLSearchParams();
  if (kind) usp.set('kind', kind);
  if (type) usp.set('type', type);
  const q = params?.q?.trim();
  if (q) usp.set('q', q);
  if (params?.dateField) usp.set('date_field', params.dateField);
  if (params?.from) usp.set('from', params.from);
  if (params?.to) usp.set('to', params.to);
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/** A portal fetch failure that preserves the HTTP status (mirrors the core
 *  `ErpApiError` shape — same message format, an Error subclass so every
 *  existing `e instanceof Error` catch-site keeps working). */
export class PortalApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'PortalApiError';
  }
}

/** True when a rejection is an AUTH failure (401/403) — a session-expiry/
 *  revocation signal the SWR screens must SURFACE even over cached rows (a
 *  network blip they may suppress). */
export function isAuthError(e: unknown): boolean {
  return e instanceof PortalApiError && (e.status === 401 || e.status === 403);
}

export class PortalErpService {
  constructor(
    private readonly getToken: () => string | null,
    /** invoked on a 401 (expired/invalid token) so the shell can recover the
     *  session instead of every screen rendering a dead error panel. */
    private readonly onAuthExpired?: () => void,
    /** invoked when the server refuses this session as the OTHER company's
     *  customer on a branded origin (`[WRONG-PORTAL]` 403) — the shell renders
     *  a blocking notice pointing at the customer's own portal address
     *  (server-supplied; the client never guesses). */
    private readonly onWrongPortal?: (home: string | null) => void
  ) {}

  /** Surface a `[WRONG-PORTAL]` 403 to the shell (shared by the JSON and
   *  binary fetch paths). Returns true when it consumed the error. */
  private handleWrongPortal(status: number, bodyError: string): boolean {
    if (status === 403 && bodyError.includes('[WRONG-PORTAL]')) {
      const m = bodyError.match(/https:\/\/[a-z0-9.-]+/i);
      this.onWrongPortal?.(m ? m[0] : null);
      return true;
    }
    return false;
  }

  // Session-scoped serve-cached-then-revalidate cache (the round-1 ErpService
  // session-cache shape, adapted for the portal's read-only screens): a tab
  // remount serves the last good rows/report instantly (no Loading blank) while
  // the fresh fetch revalidates in the background. Latest-wins: a slower stale
  // response never overwrites a newer one (per-key seq counter). Cached values
  // are the server's already-redacted Portal projections, held in memory for
  // this session only — nothing beyond what the screen already rendered.
  private readonly sessionCache = new Map<string, unknown>();
  private readonly sessionCacheSeq = new Map<string, number>();

  private revalidate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const seq = (this.sessionCacheSeq.get(key) ?? 0) + 1;
    this.sessionCacheSeq.set(key, seq);
    return fetcher().then(value => {
      // Only the newest in-flight fetch for this key may write the cache.
      if (this.sessionCacheSeq.get(key) === seq) {
        this.sessionCache.set(key, value);
      }
      return value;
    });
  }

  /** The session-cached impact report (undefined = never loaded this session). */
  cachedImpact(): PortalImpactReport | undefined {
    return this.sessionCache.get('impact') as PortalImpactReport | undefined;
  }
  /** The per-filter session cache key: two different server narrowings are
   *  DIFFERENT server responses (`?type=`/`?q=`/dates narrow server-side), so
   *  they must never share a cache slot. */
  private static listKey(
    base: string,
    params?: PortalListParams,
    type?: string
  ): string {
    return `${base}${portalQuery(params, type)}`;
  }

  /** The session-cached orders rows for one narrowing (undefined = never
   *  loaded this session). */
  cachedOrders(params?: PortalListParams): PortalRecord[] | undefined {
    return this.sessionCache.get(PortalErpService.listKey('orders', params)) as
      | PortalRecord[]
      | undefined;
  }

  /** The session-cached item rows for one filter (undefined = never loaded
   *  this session). No `type` = the unfiltered list. */
  cachedAssets(
    type?: PortalItemType,
    params?: PortalListParams
  ): PortalItem[] | undefined {
    return this.sessionCache.get(
      PortalErpService.listKey('assets', params, type)
    ) as PortalItem[] | undefined;
  }

  private async api<T>(path: string, init?: RequestInit): Promise<T> {
    const token = this.getToken();
    const res = await fetch(`/api${path}`, {
      headers: {
        accept: 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
      },
      ...init,
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.onAuthExpired?.();
      }
      let bodyError = '';
      try {
        bodyError = ((await res.json()) as { error?: string }).error ?? '';
      } catch {
        // non-JSON error body — nothing to inspect
      }
      this.handleWrongPortal(res.status, bodyError);
      throw new PortalApiError(
        `portal api ${init?.method ?? 'GET'} ${path}: ${res.status}`,
        res.status
      );
    }
    return (await res.json()) as T;
  }

  impact(): Promise<PortalImpactReport> {
    return this.revalidate('impact', () =>
      this.api<PortalImpactReport>('/portal/impact')
    );
  }

  /** The customer's own orders; `params` narrows SERVER-side (free text over
   *  the disclosed order fields + a business-date range). */
  listOrders(params?: PortalListParams): Promise<PortalRecord[]> {
    return this.revalidate(PortalErpService.listKey('orders', params), () =>
      this.api<PortalRecord[]>(`/portal/orders${portalQuery(params)}`)
    );
  }

  /**
   * §7.7 + IP5.6d: the customer's own items. An optional `type` narrows
   * SERVER-side via the validated `?type=` param (the closed `PortalItemType`
   * union — never a free string) so the client never re-filters rows; no
   * `type` fetches the unfiltered list. `params.q` is the server-side serial /
   * model / order-ref lookup (items take no date params).
   */
  listAssets(
    type?: PortalItemType,
    params?: PortalListParams
  ): Promise<PortalItem[]> {
    return this.revalidate(
      PortalErpService.listKey('assets', params, type),
      () => this.api<PortalItem[]>(`/portal/assets${portalQuery(params, type)}`)
    );
  }

  /** The session-cached certificate rows for one narrowing. */
  cachedCertificates(
    params?: PortalListParams
  ): PortalCertificate[] | undefined {
    return this.sessionCache.get(
      PortalErpService.listKey('certificates', params)
    ) as PortalCertificate[] | undefined;
  }

  /**
   * The customer's own issued certificates across ALL orders (newest service
   * date first), each carrying the server-built `download_path` + the parent
   * order's `order_no`/`service_date`. `params.q` matches the certificate
   * number / order number / order ref; `params.from`/`to` range the SERVICE
   * date by default (`dateField: 'issued_at'` switches to the issue stamp).
   */
  listCertificates(params?: PortalListParams): Promise<PortalCertificate[]> {
    return this.revalidate(
      PortalErpService.listKey('certificates', params),
      () =>
        this.api<PortalCertificate[]>(`/portal/certificates${portalQuery(params)}`)
    );
  }

  /** Raw binary GET with the Bearer header + the `X-Export-Truncated` signal. */
  private async download(
    path: string,
    accept: string
  ): Promise<PortalDownload> {
    const token = this.getToken();
    const res = await fetch(path, {
      headers: {
        accept,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.onAuthExpired?.();
      }
      let bodyError = '';
      try {
        bodyError = ((await res.json()) as { error?: string }).error ?? '';
      } catch {
        // binary/non-JSON error body
      }
      this.handleWrongPortal(res.status, bodyError);
      throw new PortalApiError(`portal download ${path}: ${res.status}`, res.status);
    }
    return {
      blob: await res.blob(),
      truncated: res.headers.get('x-export-truncated') === 'true',
    };
  }

  /**
   * Batch download: ONE ZIP of every matching signed certificate PDF
   * (`GET /api/portal/certificates/archive`). The server caps the archive and
   * signals truncation — surface `truncated` to the customer, never swallow it.
   */
  downloadCertificatesArchive(
    params?: PortalListParams
  ): Promise<PortalDownload> {
    return this.download(
      `/api/portal/certificates/archive${portalQuery(params)}`,
      'application/zip'
    );
  }

  /**
   * CSV export of the customer's own rows (`GET /api/portal/export?kind=` —
   * the same validated narrowing params as the matching list). Cells are the
   * server's redacted Portal projection; `truncated` mirrors
   * `X-Export-Truncated` (no silent partial).
   */
  exportCsv(
    kind: PortalExportKind,
    params?: PortalListParams,
    type?: PortalItemType
  ): Promise<PortalDownload> {
    return this.download(
      `/api/portal/export${portalQuery(params, type, kind)}`,
      'text/csv'
    );
  }

  /** The customer's account-level display config (today: the asset-prep-warning waiver —
   *  owner 2026-08-25: Dell program accounts don't get the prep warning). Session-cached. */
  profile(): Promise<PortalProfile> {
    return this.revalidate('profile', () =>
      this.api<PortalProfile>('/portal/profile')
    );
  }

  /** The session-cached quote-request rows for one narrowing (undefined =
   *  never loaded this session). */
  cachedQuoteRequests(params?: PortalListParams): PortalRecord[] | undefined {
    return this.sessionCache.get(
      PortalErpService.listKey('quote-requests', params)
    ) as PortalRecord[] | undefined;
  }

  /** P3.2: the customer's own quote/service requests (account-scoped
   *  server-side); `params.q` narrows over the declared §8/server fields. */
  listQuoteRequests(params?: PortalListParams): Promise<PortalRecord[]> {
    return this.revalidate(
      PortalErpService.listKey('quote-requests', params),
      () =>
        this.api<PortalRecord[]>(`/portal/quote-requests${portalQuery(params)}`)
    );
  }

  /**
   * P3.2: file a quote/service request on the customer's OWN account (the server derives the
   * account from the token and mints the TWSQ-## sequence + the new_request status — the body
   * carries ONLY the §8 form fields; a smuggled server-owned field is refused at the edge).
   */
  createQuoteRequest(input: PortalQuoteRequestInput): Promise<PortalRecord> {
    return this.api<PortalRecord>('/portal/quote-requests', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * P3.3: one own order + its deliverable downloads. The server emits the
   * PINNED Monday-parity projection (status + dispatch/schedule/completion
   * dates) and the deliverable list with server-built `download_path`s — the
   * client renders download affordances STRICTLY from that list (the 4B.6
   * rule) and never derives URLs itself.
   */
  orderDetail(orderId: string): Promise<PortalOrderDetail> {
    return this.api<PortalOrderDetail>(`/portal/orders/${orderId}`);
  }

  /**
   * IP5.6: the customer light passport for one own item (asset / drive / part /
   * commodity_lot). A 404 is ONE flat status — foreign, missing and
   * pre-certificate drive are indistinguishable by design (§3.8.1) — surfaced
   * by the screen as a neutral not-found state. Deliberately NOT session-cached:
   * a passport is opened per-item and must reflect the latest evidence.
   */
  itemPassport(itemId: string): Promise<PortalItemPassport> {
    return this.api<PortalItemPassport>(`/portal/items/${itemId}/passport`);
  }

  /**
   * P3.3: fetch a deliverable's bytes from its server-supplied `download_path`
   * (already `/api/...`-rooted). Rides the ONE binary `download()` helper so an
   * expired session triggers recovery and a `[WRONG-PORTAL]` 403 renders the
   * wrong-portal notice — never a generic "download failed" for an auth state
   * (audit finding 2026-08-26).
   */
  downloadDeliverable(path: string): Promise<Blob> {
    return this.download(path, 'application/octet-stream').then(d => d.blob);
  }

  /**
   * Downloads the signed Certificate of Destruction for an order (Task 10:
   * `GET /api/portal/orders/:id/certificate` → PDF, or `?bundle=1` → a zip
   * bundling the cert + its chained evidence). Rides the ONE binary
   * `download()` helper (binary body — `api<T>` does `res.json()`); throws on a
   * non-OK status (e.g. 404 when no certificate exists yet).
   */
  downloadCertificate(orderId: string, bundle = false): Promise<Blob> {
    const qs = bundle ? '?bundle=1' : '';
    return this.download(
      `/api/portal/orders/${orderId}/certificate${qs}`,
      bundle ? 'application/zip' : 'application/pdf'
    ).then(d => d.blob);
  }

  /** The public verify-key PEM, memoized after the first successful fetch (it is
   *  a static public key for the deployment — refetching it per click buys
   *  nothing; a failed fetch is NOT cached, so the next click retries). */
  private verifyKeyPem: Blob | null = null;

  /**
   * Fetches the public verification key (Task 10:
   * `GET /api/portal/cert/verify-key` → PEM) so a customer can verify a
   * certificate's CMS signature offline (`openssl cms -verify`). Rides the ONE
   * binary `download()` helper (PEM, not JSON); memoized on success.
   */
  async downloadVerifyKey(): Promise<Blob> {
    if (this.verifyKeyPem) {
      return this.verifyKeyPem;
    }
    const pem = (await this.download('/api/portal/cert/verify-key', 'application/x-pem-file'))
      .blob;
    this.verifyKeyPem = pem;
    return pem;
  }
}
